/**
 * Exchange Rate Service
 *
 * Fetches current exchange rates for currency conversion.
 * Uses frankfurter.app API (free, no API key required)
 */

// Cache for exchange rates
let ratesCache = {
  rates: null,
  lastFetched: null,
  baseCurrency: 'TRY',
};

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

/**
 * Fetch current exchange rates from API
 * Returns rates relative to TRY (1 USD = X TRY, 1 EUR = X TRY)
 */
export async function fetchExchangeRates() {
  // Check cache
  if (ratesCache.rates && ratesCache.lastFetched) {
    const cacheAge = Date.now() - ratesCache.lastFetched;
    if (cacheAge < CACHE_DURATION) {
      return ratesCache.rates;
    }
  }

  try {
    // Frankfurter API - get rates with EUR as base, then calculate TRY rates
    const response = await fetch('https://api.frankfurter.app/latest?from=EUR&to=USD,TRY');

    if (!response.ok) {
      throw new Error('Failed to fetch exchange rates');
    }

    const data = await response.json();

    // Calculate rates relative to TRY
    // data.rates gives us: { USD: x, TRY: y } where 1 EUR = x USD and 1 EUR = y TRY
    const eurToTry = data.rates.TRY;
    const eurToUsd = data.rates.USD;
    const usdToTry = eurToTry / eurToUsd;

    const rates = {
      EUR: eurToTry,      // 1 EUR = X TRY
      USD: usdToTry,      // 1 USD = X TRY
      TRY: 1,             // 1 TRY = 1 TRY
      GBP: eurToTry * 1.17, // Approximate GBP rate (EUR * ~1.17)
      lastUpdated: new Date().toISOString(),
      source: 'frankfurter.app',
    };

    // Update cache
    ratesCache = {
      rates,
      lastFetched: Date.now(),
      baseCurrency: 'TRY',
    };

    return rates;
  } catch (error) {
    console.error('Error fetching exchange rates:', error);

    // Return fallback rates if fetch fails
    return {
      EUR: 37.5,  // Fallback EUR/TRY rate
      USD: 35.0,  // Fallback USD/TRY rate
      TRY: 1,
      GBP: 44.0,  // Fallback GBP/TRY rate
      lastUpdated: null,
      source: 'fallback',
      error: error.message,
    };
  }
}

/**
 * Convert amount from one currency to TRY
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code (USD, EUR, TRY, GBP)
 * @param {Object} rates - Exchange rates object
 * @returns {number} Amount in TRY
 */
export function convertToTRY(amount, fromCurrency, rates) {
  if (!rates || fromCurrency === 'TRY') {
    return amount;
  }

  const rate = rates[fromCurrency];
  if (!rate) {
    console.warn(`Unknown currency: ${fromCurrency}, returning original amount`);
    return amount;
  }

  return amount * rate;
}

/**
 * Convert amount from TRY to another currency
 * @param {number} amount - Amount in TRY
 * @param {string} toCurrency - Target currency code
 * @param {Object} rates - Exchange rates object
 * @returns {number} Amount in target currency
 */
export function convertFromTRY(amount, toCurrency, rates) {
  if (!rates || toCurrency === 'TRY') {
    return amount;
  }

  const rate = rates[toCurrency];
  if (!rate) {
    console.warn(`Unknown currency: ${toCurrency}, returning original amount`);
    return amount;
  }

  return amount / rate;
}

/**
 * Format currency with proper symbol and locale
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'TRY') {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Get cached rates without fetching
 * @returns {Object|null} Cached rates or null
 */
export function getCachedRates() {
  return ratesCache.rates;
}

/**
 * Clear the rates cache
 */
export function clearRatesCache() {
  ratesCache = {
    rates: null,
    lastFetched: null,
    baseCurrency: 'TRY',
  };
}

export default {
  fetchExchangeRates,
  convertToTRY,
  convertFromTRY,
  formatCurrency,
  getCachedRates,
  clearRatesCache,
};
